import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createBaseSelector,
  registerSlice,
  StatusType
} from '@shared/lib/redux'

import { LAYER_NAME, LAYER_OPACITY } from '../config'
import { ClusterModel } from '../domain/cluster.model'
import {
  computeClustersIconLayer,
  computeClustersLayer,
  computeClustersTextLayer,
  computeSelectedCircleLayer
} from '../lib'
import { ClusterParams, ClustersPayloadType, LAYER_SIZE } from '../types'

export type ClusterStore = {
  selectedCluster: ClusterModel | null
  clusters: {
    [LAYER_SIZE.LITTLE]: ClusterModel[]
    [LAYER_SIZE.MEDIUM]: ClusterModel[]
    [LAYER_SIZE.LARGE]: ClusterModel[]
  }
  status: StatusType
  error: string | null
}

const initialState: ClusterStore = {
  selectedCluster: null,
  clusters: {
    [LAYER_SIZE.LITTLE]: [],
    [LAYER_SIZE.MEDIUM]: [],
    [LAYER_SIZE.LARGE]: []
  },
  status: 'idle',
  error: null
}

export const clusterSlice = createSlice({
  name: 'clusterSlice',
  initialState,
  reducers: {
    selectCluster: (state, action: PayloadAction<ClusterModel>) => {
      state.selectedCluster = action.payload
    },
    getClustersStart: (state, _action: PayloadAction<ClusterParams>) => {
      state.status = 'fetching'
    },
    getClustersSuccess: (state, action: PayloadAction<ClustersPayloadType>) => {
      state.status = 'success'
      state.clusters['small'] = action.payload.small
      state.clusters['medium'] = action.payload.medium
      state.clusters['big'] = action.payload.big
    },
    getClustersFailed: (state, _action: PayloadAction<string>) => {
      state.status = 'error'
    },
    getClustersCanceled: (state) => {
      state.status = 'idle'
    },
    setSelectedCluster: (state, action: PayloadAction<ClusterModel | null>) => {
      state.selectedCluster = action.payload
    },
    reset: (_state) => {
      return initialState
    }
  }
})

const clusterBaseSelector = createBaseSelector(clusterSlice)
registerSlice([clusterSlice])

const selectSelectedCluster = createSelector(clusterBaseSelector, (s) => {
  return s.selectedCluster
})

// const selectPainterLayers = createSelector(
//   painterBaseSelector,
//   (_: unknown, usersCount: number, currentZoom: number) => ({
//     usersCount,
//     currentZoom
//   }),
//   (s, { usersCount, currentZoom }) => {
//     const painter = s.painter
//     if (!painter) {
//       return {
//         measurerIconLayer: null,
//         radiusTextLayer: null,
//         usersCountTextLayer: null,
//         usersCountIconLayer: null
//       }
//     }
//     return {
//       measurerIconLayer: computeMeasureIconLayer(painter),
//       radiusTextLayer: computeAreaRadiusLayer(painter, currentZoom),
//       usersCountTextLayer: computeCircleUserCountLayer(
//         painter,
//         usersCount,
//         currentZoom
//       ),
//       usersCountIconLayer: computeFoundUsersInAreaIconLayer(
//         painter,
//         usersCount,
//         currentZoom
//       )
//     }
//   }
// )

const selectLittleClusterLayers = createSelector(
  clusterBaseSelector,
  (_: unknown, currentZoom: number) => currentZoom,
  (s, currentZoom) => {
    const smallClusters = s.clusters['small']
    return {
      circlesLayer: computeClustersLayer(
        LAYER_NAME.CIRCLES_LITTLE,
        smallClusters,
        LAYER_OPACITY.LITTLE
      ),
      textLayer: computeClustersTextLayer(
        smallClusters,
        LAYER_NAME.TEXT_LITTLE,
        currentZoom
      ),
      iconLayer: computeClustersIconLayer(
        smallClusters,
        LAYER_NAME.ICON_LITTLE,
        currentZoom
      )
    }
  }
)
const selectLittleClusters = createSelector(
  clusterBaseSelector,
  (s) => s.clusters['small']
)
const selectMediumClusterLayers = createSelector(
  clusterBaseSelector,
  (_: unknown, currentZoom: number) => currentZoom,
  (s, currentZoom) => {
    const mediumClusters = s.clusters['medium']
    return {
      circlesLayer: computeClustersLayer(
        LAYER_NAME.CIRCLES_MEDIUM,
        mediumClusters,
        LAYER_OPACITY.MEDIUM
      ),
      textLayer: computeClustersTextLayer(
        mediumClusters,
        LAYER_NAME.TEXT_MEDIUM,
        currentZoom
      ),
      iconLayer: computeClustersIconLayer(
        mediumClusters,
        LAYER_NAME.CLUSTER_MEDIUM,
        currentZoom
      )
    }
  }
)
const selectMediumClusters = createSelector(
  clusterBaseSelector,
  (s) => s.clusters['medium']
)
const selectLargeClusterLayers = createSelector(
  clusterBaseSelector,
  (_: unknown, currentZoom: number) => currentZoom,
  (s, currentZoom) => {
    const largeClusters = s.clusters['big']
    return {
      circlesLayer: computeClustersLayer(
        LAYER_NAME.CIRCLES_LARGE,
        largeClusters,
        LAYER_OPACITY.LARGE
      ),
      textLayer: computeClustersTextLayer(
        largeClusters,
        LAYER_NAME.TEXT_LARGE,
        currentZoom
      ),
      iconLayer: computeClustersIconLayer(
        largeClusters,
        LAYER_NAME.CLUSTER_LARGE,
        currentZoom
      )
    }
  }
)

const selectLargeClusters = createSelector(
  clusterBaseSelector,
  (s) => s.clusters['big']
)
const selectSelectedClusterLayer = createSelector(clusterBaseSelector, (s) => {
  return {
    layer: computeSelectedCircleLayer(s.selectedCluster)
  }
})

const selectStatus = createSelector(clusterBaseSelector, (s) => s.status)

export const clusterStore = {
  actions: { ...clusterSlice.actions },
  selectors: {
    selectSelectedCluster,
    selectLittleClusters,
    selectLittleClusterLayers,
    selectMediumClusters,
    selectMediumClusterLayers,
    selectLargeClusters,
    selectLargeClusterLayers,
    selectSelectedClusterLayer,
    selectStatus
  },
  name: clusterSlice.name
}
